import { DemoContentCard } from "@/components/DemoContentCard";
import { ImportReport } from "@/components/ImportReport";
import { ShopSettingsForm } from "@/components/ShopSettingsForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/input";
import { ErrorBox } from "@/components/ui/misc";
import { type Me, api, q } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  DemoApp,
  DiscoverDetailsResult,
  DiscoveredLocation,
  DiscoveredSource,
  ImportJobDTO,
  MergePreviewDTO,
  ProspectDTO,
} from "@mm-sales/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Globe,
  MapPin,
  Monitor,
  Search,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Store,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

type Step = "business" | "import" | "apps";
const RESUME_KEY = "mm-sales:new-demo";

const APPS: { id: DemoApp; label: string; hint: string; icon: typeof Globe }[] = [
  { id: "website", label: "Website", hint: "Their branded site with online ordering", icon: Globe },
  { id: "pos", label: "SUNMI POS", hint: "Point of sale on a streamed device", icon: Monitor },
  { id: "admin", label: "Admin app", hint: "The MyManager dashboard on their data", icon: Store },
  {
    id: "client",
    label: "Customer app",
    hint: "Ordering, reservations, rewards",
    icon: Smartphone,
  },
];

/** Paste a URL → we find the business → pick apps → demo built. Under a minute. */
export function NewDemoPage() {
  const me = useOutletContext<Me>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("business");
  const [err, setErr] = useState<string | null>(null);

  const [form, setForm] = useState({
    businessName: "",
    website: "",
    city: "",
    state: "",
    cuisine: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    locationsCount: 1,
  });
  const [prospect, setProspect] = useState<ProspectDTO | null>(null);
  // The wizard survives a reload or a remount (the host renews the session token
  // every few minutes): the prospect it created and the step it was on are kept.
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (restored) return;
    let saved: { id: string; step: Step } | null = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(RESUME_KEY) ?? "null");
    } catch {
      saved = null;
    }
    if (!saved?.id) {
      setRestored(true);
      return;
    }
    q.prospect(saved.id)
      .then((pr) => {
        if (pr.outcome === "open" && !pr.activeDemo) {
          setProspect(pr);
          setStep(saved!.step === "business" ? "import" : saved!.step);
        } else sessionStorage.removeItem(RESUME_KEY);
      })
      .catch(() => sessionStorage.removeItem(RESUME_KEY))
      .finally(() => setRestored(true));
  }, [restored]);
  useEffect(() => {
    if (prospect) sessionStorage.setItem(RESUME_KEY, JSON.stringify({ id: prospect.id, step }));
  }, [prospect, step]);
  // "Find this business online": ranked sources; the agent picks one to import from.
  const [sources, setSources] = useState<DiscoveredSource[] | null>(null);
  // Several sources can be ticked: each is imported and the results are merged
  // (menu from the richest, brand from the website, hours from Google). The
  // first ticked one is the "primary" — it fills the form and picks locations.
  const [picked, setPicked] = useState<DiscoveredSource[]>([]);
  const source = picked[0] ?? null;
  const sameSource = (a: DiscoveredSource, b: DiscoveredSource) => a.id === b.id && a.url === b.url;
  // Multi-location. Every location the selected source lists is built by
  // default; locations only other sources know are offered underneath. Each
  // ticked location becomes its own MyManager location with its own menu.
  const [locations, setLocations] = useState<DiscoveredLocation[]>([]);
  const [pickedLocs, setPickedLocs] = useState<Record<string, boolean>>({});
  const selPlatform = source?.platform;
  const primaryLocs = locations.filter(
    (g) => selPlatform && g.sources.some((e) => e.platform === selPlatform),
  );
  const otherLocs = locations.filter((g) => !primaryLocs.includes(g));
  const showLocs = Boolean(source) && locations.length >= 2;
  /** Where this location's menu comes from: the selected source if it can be read, else the best other one. */
  const menuSourceFor = (g: DiscoveredLocation) =>
    g.sources.find((e) => e.importable && e.platform === selPlatform) ??
    g.sources.find((e) => e.importable) ??
    null;
  const locOn = (g: DiscoveredLocation, primary: boolean) => pickedLocs[g.key] ?? primary;
  const chosenLocs = showLocs
    ? [...primaryLocs.filter((g) => locOn(g, true)), ...otherLocs.filter((g) => locOn(g, false))]
    : [];
  const discover = useMutation({
    mutationFn: () =>
      api.post<{ sources: DiscoveredSource[]; locations: DiscoveredLocation[] }>("/v1/discover", {
        businessName: form.businessName,
        city: form.city || undefined,
        state: form.state || undefined,
        website: form.website || undefined,
      }),
    onSuccess: (r) => {
      setSources(r.sources);
      setLocations(r.locations ?? []);
      setErr(null);
      setPickedLocs({});
      // Default to "get everything": the best store page, their website and Google.
      const first = r.sources.find((x) => x.importable) ?? null;
      const auto: DiscoveredSource[] = [];
      for (const x of [
        first,
        r.sources.find((x) => x.importable && x.platform === "website"),
        r.sources.find((x) => x.importable && x.platform === "google_places"),
      ])
        if (x && !auto.some((y) => y.platform === x.platform)) auto.push(x);
      setPicked(auto);
      if (first) details.mutate(first);
      // Google or search may know their website when the field is empty.
      const site =
        r.sources.find((x) => x.platform === "website")?.url ??
        r.sources.find((x) => x.website)?.website;
      if (!form.website && site) setForm((f) => ({ ...f, website: site }));
    },
    onError: (e) => setErr(e.message),
  });
  // Selecting a source reads its business details and fills the form below.
  const [previews, setPreviews] = useState<Record<string, DiscoverDetailsResult>>({});
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  const applyDetails = (x: DiscoveredSource, r: DiscoverDetailsResult) => {
    const b = r.business;
    if (!b) return;
    setForm((f) => ({
      ...f,
      businessName: f.businessName || b.name,
      website: x.platform === "website" ? x.url : b.website || f.website,
      city: b.address.city || f.city,
      state: b.address.state || f.state,
      cuisine: b.cuisine || f.cuisine,
      phone: b.phone || f.phone,
      email: b.email || f.email,
    }));
    setFilledFrom(x.label);
  };
  const details = useMutation({
    mutationFn: async (x: DiscoveredSource) => {
      const key = `${x.id}:${x.url}`;
      const cached = previews[key];
      const r =
        cached ??
        (await api.post<DiscoverDetailsResult>("/v1/discover/details", {
          platform: x.platform,
          url: x.platform === "google_places" ? undefined : x.url,
          businessName: form.businessName,
          city: form.city || undefined,
          state: form.state || undefined,
        }));
      if (!cached) setPreviews((p) => ({ ...p, [key]: r }));
      return { x, r };
    },
    onSuccess: ({ x, r }) => applyDetails(x, r),
  });
  const pickSource = (x: DiscoveredSource) => {
    const on = picked.some((y) => sameSource(x, y));
    if (on) {
      setPicked((ps) => ps.filter((y) => !sameSource(x, y)));
      return;
    }
    // One store page per platform: a second Uber Eats row replaces the first
    // (those are per-location stores; multi-location is handled below).
    setPicked((ps) => [...ps.filter((y) => y.platform !== x.platform), x]);
    if (picked.length === 0 || picked.every((y) => y.platform === x.platform)) {
      setFilledFrom(null);
      setPickedLocs({});
      details.mutate(x);
    }
    if (x.platform === "website" && x.url) setForm((f) => ({ ...f, website: x.url }));
    else if (x.website && !form.website) setForm((f) => ({ ...f, website: x.website! }));
  };
  // One import job per source; when more than one lands in review they are merged.
  const [jobs, setJobs] = useState<ImportJobDTO[]>([]);
  const job = jobs[0] ?? null;
  const upsertJob = (j: ImportJobDTO) =>
    setJobs((js) =>
      js.some((x) => x.id === j.id) ? js.map((x) => (x.id === j.id ? j : x)) : [...js, j],
    );
  const busyJobs = jobs.filter((j) => j.status === "queued" || j.status === "running");
  const reviewJobs = jobs.filter((j) => j.status === "review");
  const merge = useQuery({
    queryKey: ["merge-preview", prospect?.id, reviewJobs.map((j) => j.id).join(",")],
    queryFn: () =>
      q.mergePreview(
        prospect!.id,
        reviewJobs.map((j) => j.id),
      ),
    enabled: Boolean(prospect) && reviewJobs.length >= 2 && busyJobs.length === 0,
  });
  const [apps, setApps] = useState<DemoApp[]>(["website", "pos", "admin", "client"]);
  const [days, setDays] = useState(me.settings.demoDefaultDays);
  // Optional, paid: draw photos for imported items that have none before the demo is built.
  const [genPhotos, setGenPhotos] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const detect = useQuery({
    queryKey: ["detect", form.website],
    queryFn: () => q.detect(form.website),
    enabled: /^https?:\/\//.test(form.website),
  });

  // Multi-location import: one job per location, run one after another and
  // tracked on the prospect (locations[].status), which we re-read as it moves.
  const [locProgress, setLocProgress] = useState<Record<string, ImportJobDTO>>({});
  const importLocations = useMutation({
    mutationFn: async () => {
      const pr = prospect!;
      for (const loc of pr.locations) {
        if (!loc.source) continue;
        const j0 = await api.post<ImportJobDTO>("/v1/imports", {
          prospectId: pr.id,
          platform: loc.source.platform,
          sourceUrl: loc.source.url,
          locationId: loc.id,
        });
        let j = j0;
        setLocProgress((m) => ({ ...m, [loc.id]: j }));
        while (j.status === "queued" || j.status === "running") {
          await new Promise((r) => setTimeout(r, 1200));
          j = await q.importJob(j.id);
          setLocProgress((m) => ({ ...m, [loc.id]: j }));
        }
      }
      return q.prospect(pr.id);
    },
    onSuccess: (pr) => setProspect(pr),
    onError: (e) => setErr(e.message),
  });
  const multi = (prospect?.locations.length ?? 0) >= 2;
  const multiDone =
    multi &&
    prospect!.locations.every((l) => {
      if (!l.source) return true; // nothing to import: starts with a blank menu
      const j = locProgress[l.id];
      return j ? j.status === "review" || j.status === "failed" || j.status === "applied" : false;
    });

  const createProspect = useMutation({
    mutationFn: () =>
      api.post<ProspectDTO>("/v1/prospects", {
        businessName: form.businessName,
        website: form.website || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        cuisine: form.cuisine || undefined,
        locationsCount: Math.max(form.locationsCount, chosenLocs.length || 1),
        locations:
          chosenLocs.length >= 2
            ? chosenLocs.map((g) => {
                const m = menuSourceFor(g);
                // The site's own page for this location (schedule / events of that branch), when it has one.
                const root = (form.website || "").replace(/\/+$/, "").toLowerCase();
                const page = g.sources.find(
                  (s) =>
                    s.platform === "website" &&
                    s.url &&
                    s.url.replace(/\/+$/, "").toLowerCase() !== root,
                );
                return {
                  name: g.name,
                  address: g.address,
                  phone: g.phone || undefined,
                  pageUrl: page?.url,
                  source: m && m.platform !== "other" ? { platform: m.platform, url: m.url } : null,
                };
              })
            : undefined,
        contact: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
        },
        source: source?.importable ? source.platform : form.website ? "website" : "manual",
        sourceUrl: source?.importable ? source.url : form.website || undefined,
      }),
    onSuccess: (p) => {
      setProspect(p);
      setStep("import");
      setErr(null);
    },
    onError: (e) => setErr(e.message),
  });

  const [storeUrl, setStoreUrl] = useState("");
  // Mirrors detectPlatform() in @mm-sales/importers (server-only package).
  const marketplace = /ubereats\.com/i.test(storeUrl)
    ? ("ubereats" as const)
    : /doordash\.com/i.test(storeUrl)
      ? ("doordash" as const)
      : /grubhub\.com|seamless\.com/i.test(storeUrl)
        ? ("grubhub" as const)
        : null;
  const startImport = useMutation({
    mutationFn: (
      arg:
        | "website"
        | "google_places"
        | "ubereats"
        | "doordash"
        | "grubhub"
        | { platform: DiscoveredSource["platform"]; sourceUrl: string },
    ) => {
      const platform = typeof arg === "string" ? arg : arg.platform;
      const sourceUrl =
        typeof arg !== "string"
          ? arg.sourceUrl
          : platform === "website"
            ? form.website
            : platform === "google_places"
              ? undefined
              : storeUrl;
      return api.post<ImportJobDTO>("/v1/imports", {
        prospectId: prospect!.id,
        platform,
        sourceUrl: platform === "google_places" ? undefined : sourceUrl,
      });
    },
    onSuccess: (j) => upsertJob(j),
    onError: (e) => setErr(e.message),
  });
  // All ticked sources at once, in parallel.
  const startAll = useMutation({
    mutationFn: async () => {
      const started = await Promise.all(
        picked.map((x) =>
          api.post<ImportJobDTO>("/v1/imports", {
            prospectId: prospect!.id,
            platform: x.platform,
            sourceUrl: x.platform === "google_places" ? undefined : x.url,
          }),
        ),
      );
      return started;
    },
    onSuccess: (js) => {
      for (const j of js) upsertJob(j);
      setErr(null);
    },
    onError: (e) => setErr(e.message),
  });

  // Poll every running import until it settles. A tick counter drives the next
  // poll — keying on progress alone stopped polling the moment a slow step (a
  // 60 s PDF read, a browser render) returned the same percentage twice.
  const [pollTick, setPollTick] = useState(0);
  const busyKey = busyJobs.map((j) => j.id).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: busyKey + pollTick are the flattened dependencies
  useEffect(() => {
    if (!busyJobs.length) return;
    const t = setTimeout(async () => {
      try {
        const fresh = await Promise.all(busyJobs.map((j) => q.importJob(j.id)));
        for (const j of fresh) upsertJob(j);
      } finally {
        setPollTick((n) => n + 1);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [busyKey, pollTick]);

  const [genProgress, setGenProgress] = useState<string | null>(null);
  const createDemo = useMutation({
    mutationFn: async () => {
      if (genPhotos && me.capabilities.images) {
        for (const j of reviewJobs) {
          const missing = (j.shop?.items ?? []).filter((it) => !it.imageUrl).length;
          if (!missing) continue;
          let job = await api.post<ImportJobDTO>(`/v1/imports/${j.id}/generate-images`, {
            limit: 40,
          });
          while (job.imageGen?.status === "running") {
            setGenProgress(
              `Drawing photos for ${sourceLabel(j.platform)}: ${job.imageGen.done}/${job.imageGen.total}`,
            );
            await new Promise((r) => setTimeout(r, 2500));
            job = await q.importJob(j.id);
            upsertJob(job);
          }
        }
        setGenProgress(null);
      }
      if (reviewJobs.length >= 2)
        await api.post("/v1/imports/apply-merged", {
          prospectId: prospect!.id,
          jobIds: reviewJobs.map((j) => j.id),
        });
      else if (reviewJobs[0]) await api.post(`/v1/imports/${reviewJobs[0].id}/apply`);
      return api.post<{ id: string }>("/v1/demos", { prospectId: prospect!.id, apps, days });
    },
    onSuccess: () => {
      sessionStorage.removeItem(RESUME_KEY);
      qc.invalidateQueries({ queryKey: ["prospects"] });
      nav(`/app/prospects/${prospect!.id}`);
    },
    onError: (e) => setErr(e.message),
  });

  const steps: { id: Step; label: string }[] = [
    { id: "business", label: "Business" },
    { id: "import", label: "Import" },
    { id: "apps", label: "Apps & build" },
  ];

  const locRow = (g: DiscoveredLocation, primary: boolean) => {
    const on = locOn(g, primary);
    const m = menuSourceFor(g);
    const where = [
      g.address.line1,
      [g.address.city, [g.address.state, g.address.postalCode].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", "),
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <li key={g.key}>
        <label className="flex cursor-pointer items-start gap-3 py-2">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => setPickedLocs((p) => ({ ...p, [g.key]: e.target.checked }))}
            className="mt-1 h-4 w-4 accent-primary"
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-medium">{g.name}</span>
              {where ? <span className="text-xs text-muted-foreground">{where}</span> : null}
              {g.phone ? <span className="text-xs text-muted-foreground">{g.phone}</span> : null}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {m ? (
                <>
                  Menu from {m.label} ·{" "}
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    {m.url}
                  </a>
                </>
              ) : (
                "No menu source found — starts with a blank menu"
              )}
            </span>
          </span>
        </label>
      </li>
    );
  };

  return (
    <div className="mx-auto max-w-3xl p-6 lg:p-8">
      <Link
        to="/app"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> My demos
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">New demo</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Build a demo of the prospect's own business — their menu, their branding.
      </p>

      <ol className="mb-6 flex gap-2">
        {steps.map((s, i) => {
          const idx = steps.findIndex((x) => x.id === step);
          const done = i < idx;
          return (
            <li
              key={s.id}
              className={cn(
                "flex flex-1 items-center gap-2 rounded-md border px-3 py-2 text-sm",
                i === idx
                  ? "border-primary bg-accent text-accent-foreground"
                  : done
                    ? "text-success"
                    : "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
                  i === idx
                    ? "bg-primary text-primary-foreground"
                    : done
                      ? "bg-success text-success-foreground"
                      : "bg-muted",
                )}
              >
                {done ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {err ? (
        <div className="mb-4">
          <ErrorBox message={err} />
        </div>
      ) : null}

      {step === "business" ? (
        <Card>
          <CardHeader>
            <CardTitle>Who is this demo for?</CardTitle>
            <CardDescription>
              A name is enough to start. A website lets us pull their menu and branding.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label="Business name">
                <Input
                  autoFocus
                  value={form.businessName}
                  onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                  placeholder="Bella Bistro"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field
                label="Website (optional)"
                hint={
                  detect.data?.adapter
                    ? `Detected: ${detect.data.adapter.label}${detect.data.adapter.reliability === "best_effort" ? " — best effort" : ""}`
                    : undefined
                }
              >
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value.trim() })}
                  placeholder="https://bellabistro.com or an Uber Eats / DoorDash link"
                />
              </Field>
              <div className="mt-3">
                <Button
                  variant="outline"
                  disabled={form.businessName.trim().length < 2}
                  loading={discover.isPending}
                  onClick={() => discover.mutate()}
                >
                  <Search className="h-4 w-4" />
                  {sources ? "Search again" : "Find this business online"}
                </Button>
                <span className="ml-3 text-xs text-muted-foreground">
                  Their website, Google, Uber Eats, DoorDash, Grubhub — tick every source worth
                  reading. We import them all and merge: the menu from the richest, photos from
                  wherever they are, brand from their website, hours from Google.
                </span>
              </div>
              {sources ? (
                <div className="mt-3 overflow-hidden rounded-lg border">
                  {sources.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">
                      Nothing found online for "{form.businessName}". Add their city, or paste a
                      website or store link above.
                    </div>
                  ) : (
                    sources.map((x) => {
                      const selected = picked.some((y) => sameSource(x, y));
                      const primary = selected && source ? sameSource(x, source) : false;
                      const Row = x.importable ? "button" : "div";
                      return (
                        <Row
                          key={`${x.id}:${x.url}`}
                          type={x.importable ? "button" : undefined}
                          onClick={x.importable ? () => pickSource(x) : undefined}
                          className={cn(
                            "flex w-full items-start gap-3 border-b p-3 text-left last:border-b-0",
                            x.importable ? "hover:bg-muted" : "bg-muted/40 text-muted-foreground",
                            selected && "bg-accent",
                          )}
                        >
                          <span
                            className={cn(
                              "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                              selected ? "border-primary bg-primary" : "border-input",
                              !x.importable && "invisible",
                            )}
                            title={
                              primary
                                ? "Primary source (fills the form, picks locations)"
                                : selected
                                  ? "Will be imported and merged"
                                  : "Tick to import and merge"
                            }
                          >
                            {selected ? <Check className="h-3 w-3 text-white" /> : null}
                          </span>
                          {x.imageUrl ? (
                            <img
                              src={x.imageUrl}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
                              {x.platform === "google_places" ? (
                                <MapPin className="h-4 w-4 text-primary" />
                              ) : x.platform === "website" ? (
                                <Globe className="h-4 w-4 text-primary" />
                              ) : (
                                <Sparkles className="h-4 w-4 text-primary" />
                              )}
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-foreground">{x.label}</span>
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                                  x.reliability === "api"
                                    ? "bg-success/10 text-success"
                                    : x.reliability === "best_effort"
                                      ? "bg-warning/10 text-warning"
                                      : "bg-muted text-muted-foreground",
                                )}
                              >
                                {x.reliability === "api"
                                  ? "Verified"
                                  : x.reliability === "best_effort"
                                    ? "Best effort"
                                    : "Reference"}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {x.via === "website"
                                  ? "from their website"
                                  : x.via === "google"
                                    ? "from Google"
                                    : "found by search"}
                              </span>
                            </span>
                            <span className="block truncate text-sm">{x.title}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {(() => {
                                const pv = previews[`${x.id}:${x.url}`];
                                if (
                                  details.isPending &&
                                  details.variables?.id === x.id &&
                                  details.variables?.url === x.url
                                )
                                  return "Reading details…";
                                if (pv?.error) return `Couldn't read: ${pv.error}`;
                                if (pv?.business)
                                  return [
                                    [pv.business.address.city, pv.business.address.state]
                                      .filter(Boolean)
                                      .join(", "),
                                    pv.business.cuisine,
                                    pv.business.phone,
                                    pv.items ? `${pv.items} menu items` : "",
                                    pv.images ? `${pv.images} photos` : "",
                                    pv.hours ? "hours" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" · ");
                                return x.detail;
                              })()}
                            </span>
                            <a
                              href={x.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="block truncate text-xs text-primary hover:underline"
                            >
                              {x.url}
                            </a>
                          </span>
                        </Row>
                      );
                    })
                  )}
                </div>
              ) : null}
              {sources && !sources.some((x) => x.importable && x.platform !== "website") ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  No Uber Eats / DoorDash / Grubhub store page came back this time — search engines
                  rate-limit us now and then. Try "Search again" in a minute, or paste their store
                  link in the Website field to import from it directly.
                </div>
              ) : null}
              {filledFrom ? (
                <div className="mt-2 text-xs text-success">
                  Details below filled from {filledFrom}. Edit anything that's off.
                </div>
              ) : null}
              {showLocs && source ? (
                <div className="mt-4 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {primaryLocs.length
                          ? `${primaryLocs.length} locations on ${source.label} — build them all?`
                          : `${source.label} lists no locations — pick from what others found`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Each ticked location becomes its own location in the demo organization, with
                        its own address and its own menu from the store page shown.
                      </div>
                    </div>
                    <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
                      {chosenLocs.length} selected
                    </span>
                  </div>
                  {primaryLocs.length ? (
                    <ul className="mt-3 divide-y">{primaryLocs.map((g) => locRow(g, true))}</ul>
                  ) : null}
                  {otherLocs.length ? (
                    <div className={cn(primaryLocs.length && "mt-3 border-t pt-3")}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Also found on other sources, not on {source.label}
                      </div>
                      <ul className="divide-y">{otherLocs.map((g) => locRow(g, false))}</ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <Field label="City">
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Miami"
              />
            </Field>
            <Field label="State">
              <Input
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                placeholder="FL"
              />
            </Field>
            <Field label="Cuisine / type">
              <Input
                value={form.cuisine}
                onChange={(e) => setForm({ ...form, cuisine: e.target.value })}
                placeholder="Italian"
              />
            </Field>
            <Field label="Locations">
              <Input
                type="number"
                min={1}
                value={form.locationsCount}
                onChange={(e) =>
                  setForm({ ...form, locationsCount: Math.max(1, Number(e.target.value)) })
                }
              />
            </Field>
            <Field label="Contact first name">
              <Input
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              />
            </Field>
            <Field label="Contact last name">
              <Input
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </Field>
            <Field label="Contact email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Contact phone">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <div className="flex justify-end md:col-span-2">
              <Button
                disabled={!form.businessName.trim()}
                loading={createProspect.isPending}
                onClick={() => createProspect.mutate()}
              >
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === "import" && prospect ? (
        <Card>
          <CardHeader>
            <CardTitle>Pull in their details</CardTitle>
            <CardDescription>
              {multi
                ? `${prospect.locations.length} locations — each gets its own menu from its own store page.`
                : "We'll show you what we found before anything goes into the demo."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {multi ? (
              <div className="rounded-lg border">
                <ul className="divide-y">
                  {prospect.locations.map((l) => {
                    const j = locProgress[l.id];
                    const st = j?.status ?? l.status;
                    return (
                      <li key={l.id} className="flex items-center gap-3 p-3">
                        <span
                          className={cn(
                            "h-2.5 w-2.5 shrink-0 rounded-full",
                            st === "review" || st === "applied" || st === "imported"
                              ? "bg-success"
                              : st === "failed"
                                ? "bg-destructive"
                                : st === "running" || st === "queued" || st === "importing"
                                  ? "animate-pulse bg-primary"
                                  : "bg-muted-foreground/40",
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">{l.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {j?.message ||
                              (l.source
                                ? `${l.source.platform} · ${l.source.url}`
                                : "No menu source found — starts with a blank menu")}
                          </span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {j?.shop ? `${j.shop.items.length} items` : st === "pending" ? "" : st}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-between border-t p-3">
                  <span className="text-xs text-muted-foreground">
                    Runs one location at a time; blocked store pages are opened in a browser.
                  </span>
                  <Button
                    loading={importLocations.isPending}
                    disabled={multiDone}
                    onClick={() => importLocations.mutate()}
                  >
                    {multiDone
                      ? "All locations imported"
                      : `Import ${prospect.locations.length} locations`}
                  </Button>
                </div>
              </div>
            ) : null}
            {!multi && !jobs.length && picked.length ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-accent p-4">
                <Sparkles className="h-5 w-5 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    Import from{" "}
                    {picked.length === 1 ? picked[0]!.label : `${picked.length} sources`}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {picked.map((x) => (
                      <li key={`${x.id}:${x.url}`} className="truncate">
                        <span className="font-medium text-foreground">{x.label}</span> · {x.title}
                        {x.platform === "google_places" ? "" : ` · ${x.url}`}
                      </li>
                    ))}
                  </ul>
                  {picked.length > 1 ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Merged after import: menu from the richest source, photos and descriptions
                      filled from the others, brand from the website, hours from Google.
                    </div>
                  ) : null}
                </div>
                <Button loading={startAll.isPending} onClick={() => startAll.mutate()}>
                  Import {picked.length > 1 ? "all" : ""} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
            {!multi && !jobs.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                <button
                  type="button"
                  disabled={!me.capabilities.importers.google_places}
                  onClick={() => startImport.mutate("google_places")}
                  className="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted disabled:opacity-50"
                >
                  <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <div className="font-medium">Google Business Profile</div>
                    <div className="text-xs text-muted-foreground">
                      Hours, phone, address, photos, rating. Works for any business.
                      {!me.capabilities.importers.google_places ? " (not configured)" : ""}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  disabled={!form.website}
                  onClick={() => startImport.mutate("website")}
                  className="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted disabled:opacity-50"
                >
                  <Globe className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <div className="font-medium">Their website</div>
                    <div className="text-xs text-muted-foreground">
                      Branding, description, and the menu when the site publishes one.
                      {!form.website ? " (add a website first)" : ""}
                    </div>
                  </div>
                </button>
                <div className="flex flex-col gap-2 rounded-lg border p-4 md:col-span-2">
                  <div className="flex items-center gap-2 font-medium">
                    <ShoppingBag className="h-5 w-5 text-primary" /> Uber Eats, DoorDash or Grubhub
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Paste their storefront link. We read the menu, photos, hours and address the way
                    a customer's browser sees them. Best effort — these platforms sometimes block
                    it.
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://www.ubereats.com/store/…  ·  https://www.doordash.com/store/…  ·  https://www.grubhub.com/restaurant/…"
                      value={storeUrl}
                      onChange={(e) => setStoreUrl(e.target.value)}
                    />
                    <Button
                      disabled={!marketplace}
                      loading={startImport.isPending}
                      onClick={() => marketplace && startImport.mutate(marketplace)}
                    >
                      Import
                    </Button>
                  </div>
                  {storeUrl && !marketplace ? (
                    <div className="text-xs text-destructive">
                      That doesn't look like an Uber Eats, DoorDash or Grubhub store link.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : jobs.length ? (
              <div className="space-y-3">
                {jobs.map((j) => (
                  <ImportProgress key={j.id} job={j} label={sourceLabel(j.platform)} />
                ))}
                {reviewJobs.length >= 2 ? (
                  <MergedSummary merge={merge.data} loading={merge.isPending} />
                ) : null}
                {!busyJobs.length ? (
                  <div className="flex flex-wrap gap-2">
                    {sources
                      ?.filter((x) => x.importable && !jobs.some((j) => j.platform === x.platform))
                      .filter((x, i, arr) => arr.findIndex((y) => y.platform === x.platform) === i)
                      .map((x) => (
                        <Button
                          key={`${x.id}:${x.url}`}
                          size="sm"
                          variant="outline"
                          loading={startImport.isPending}
                          onClick={() =>
                            startImport.mutate(
                              x.platform === "google_places"
                                ? "google_places"
                                : { platform: x.platform, sourceUrl: x.url },
                            )
                          }
                        >
                          + {x.label}
                        </Button>
                      ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("apps")}>
                Skip — build with a blank menu
              </Button>
              <Button
                disabled={multi ? !multiDone : !jobs.length || busyJobs.length > 0}
                onClick={() => setStep("apps")}
              >
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === "apps" && prospect ? (
        <Card>
          <CardHeader>
            <CardTitle>What should {prospect.businessName} see?</CardTitle>
            <CardDescription>
              Pick the apps for this demo and how long it stays live.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 md:grid-cols-2">
              {APPS.map((a) => {
                const on = apps.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setApps(on ? apps.filter((x) => x !== a.id) : [...apps, a.id])}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                      on ? "border-primary bg-accent" : "hover:bg-muted",
                    )}
                  >
                    <a.icon
                      className={cn(
                        "mt-0.5 h-5 w-5",
                        on ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{a.label}</div>
                      <div className="text-xs text-muted-foreground">{a.hint}</div>
                    </div>
                    {on ? <Check className="h-4 w-4 text-primary" /> : null}
                  </button>
                );
              })}
            </div>
            {me.capabilities.images &&
            reviewJobs.some((j) => (j.shop?.items ?? []).some((it) => !it.imageUrl)) ? (
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-4">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-primary"
                  checked={genPhotos}
                  onChange={(e) => setGenPhotos(e.target.checked)}
                />
                <span>
                  <span className="block font-medium">Draw photos for items without one</span>
                  <span className="block text-xs text-muted-foreground">
                    {reviewJobs.reduce(
                      (n, j) => n + (j.shop?.items ?? []).filter((it) => !it.imageUrl).length,
                      0,
                    )}{" "}
                    items have no photo. AI-generated food photos, about $0.04 each (first 40 per
                    source). Optional — the demo builds either way.
                  </span>
                </span>
              </label>
            ) : null}
            <div className="rounded-lg border p-4">
              <DemoContentCard prospect={prospect} onChange={setProspect} autoStart embedded />
            </div>
            <div className="rounded-lg border">
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                onClick={() => setShowSettings((v) => !v)}
              >
                <span>
                  <span className="block font-medium">Shop settings</span>
                  <span className="block text-xs text-muted-foreground">
                    Register, POS screens, online checkout and tips — the same settings as Shop+ →
                    Settings. Leave as default or set them now.
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {prospect.shopSettings ? "customised" : "defaults"} ·{" "}
                  {showSettings ? "hide" : "edit"}
                </span>
              </button>
              {showSettings ? (
                <div className="border-t p-4">
                  <ShopSettingsForm
                    key={JSON.stringify(prospect.shopSettings ?? {})}
                    value={prospect.shopSettings ?? null}
                    onSave={async (v) => {
                      const pr = await api.patch<typeof prospect>(`/v1/prospects/${prospect.id}`, {
                        shopSettings: v,
                      });
                      setProspect(pr);
                    }}
                  />
                </div>
              ) : null}
            </div>
            <Field label="Demo lives for">
              <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {[3, 5, 7, 10, 14]
                  .filter((d) => d <= me.settings.demoMaxDays)
                  .map((d) => (
                    <option key={d} value={d}>
                      {d} days
                    </option>
                  ))}
              </Select>
            </Field>
            <div className="flex justify-end">
              <span className="mr-3 self-center text-xs text-muted-foreground">{genProgress}</span>
              <Button
                size="lg"
                disabled={apps.length === 0}
                loading={createDemo.isPending}
                onClick={() => createDemo.mutate()}
              >
                <Sparkles className="h-4 w-4" /> Build demo
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  google_places: "Google Business Profile",
  website: "Their website",
  ubereats: "Uber Eats",
  doordash: "DoorDash",
  grubhub: "Grubhub",
  menu_pdf: "Menu document",
  mymanager: "MyManager",
};
export const sourceLabel = (platform: string) => SOURCE_LABEL[platform] ?? platform;

/** What several imports merge into, and what each one contributed. */
export function MergedSummary({ merge, loading }: { merge?: MergePreviewDTO; loading: boolean }) {
  if (loading || !merge)
    return (
      <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
        Merging sources…
      </div>
    );
  const { report } = merge;
  const gave = (s: MergePreviewDTO["report"]["sources"][number]) => {
    const parts: string[] = [];
    if (s.base) parts.push(`the menu (${s.items} items)`);
    if (s.itemsAdded) parts.push(`${s.itemsAdded} more items`);
    if (s.imagesFilled) parts.push(`${s.imagesFilled} item photos`);
    if (s.descriptionsFilled) parts.push(`${s.descriptionsFilled} descriptions`);
    if (s.modifiersFilled) parts.push(`${s.modifiersFilled} option sets`);
    if (s.variantsFilled) parts.push(`${s.variantsFilled} size sets`);
    const f = s.fields.filter((x) => !["website"].includes(x));
    const brand = f.filter((x) => /brand|Font|logo|favicon/i.test(x));
    const rest = f.filter((x) => !brand.includes(x));
    if (brand.length) parts.push("the brand (colours, fonts, logo)");
    if (rest.length)
      parts.push(
        rest
          .map((x) =>
            x === "heroImageUrl" ? "cover photo" : x.replace(/([A-Z])/g, " $1").toLowerCase(),
          )
          .join(", "),
      );
    return parts.length ? parts.join(", ") : "nothing the others didn't have";
  };
  return (
    <div className="rounded-lg border border-primary/40 bg-accent p-4">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Merged result</span>
        <span className="text-muted-foreground">
          {report.totals.items} items · {report.totals.withImages} with photos ·{" "}
          {report.totals.categories} categories · {report.totals.images} gallery photos
        </span>
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {report.sources.map((s) => (
          <li key={s.platform}>
            <span className="font-medium">{sourceLabel(s.platform)}</span>
            <span className="text-muted-foreground"> gave {gave(s)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "for 1m 20s" — shown while a step runs, so a slow one reads as slow rather than stuck. */
function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.round((now - new Date(since).getTime()) / 1000));
  return (
    <span className="ml-2 tabular-nums text-muted-foreground/70">
      {s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`}
    </span>
  );
}

export function ImportProgress({
  job,
  label,
  onGenerateImages,
  generating,
}: {
  job: ImportJobDTO;
  label?: string;
  /** Present when photo generation is available for this job (OPENAI_API_KEY on the api). */
  onGenerateImages?: () => void;
  generating?: boolean;
}) {
  const busy = job.status === "queued" || job.status === "running";
  const missingPhotos = (job.shop?.items ?? []).filter((it) => !it.imageUrl).length;
  const gen = job.imageGen;
  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium">
          {label ? <span className="mr-2 text-muted-foreground">{label} ·</span> : null}
          {job.status === "failed"
            ? "Import failed"
            : job.status === "review"
              ? "Ready to review"
              : job.status === "applied"
                ? "Applied"
                : "Importing…"}
        </span>
        <span className="text-muted-foreground">
          {job.progress}%{busy ? <Elapsed since={job.createdAt} /> : null}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full transition-all",
            job.status === "failed" ? "bg-destructive" : "bg-primary",
          )}
          style={{ width: `${job.progress}%` }}
        />
      </div>
      <p
        className={cn(
          "mt-2 text-sm",
          job.status === "failed" ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {job.error ?? job.message}
      </p>
      {!busy && job.shop ? (
        <div className="mt-3 space-y-3">
          <ImportReport shop={job.shop} />
          {gen ? (
            <div
              className={cn(
                "flex items-center justify-between rounded-md px-3 py-2 text-xs",
                gen.status === "failed" ? "bg-destructive/10 text-destructive" : "bg-accent",
              )}
            >
              <span>
                {gen.status === "running"
                  ? `Drawing photos… ${gen.done}/${gen.total}`
                  : gen.status === "done"
                    ? `${gen.done} photo${gen.done === 1 ? "" : "s"} drawn${gen.failed ? `, ${gen.failed} failed` : ""}`
                    : `Photo generation failed${gen.message ? `: ${gen.message}` : ""}`}
              </span>
              {gen.status === "running" ? (
                <span className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full bg-primary transition-all"
                    style={{
                      width: `${gen.total ? Math.round((gen.done / gen.total) * 100) : 0}%`,
                    }}
                  />
                </span>
              ) : null}
            </div>
          ) : null}
          {onGenerateImages && missingPhotos > 0 && gen?.status !== "running" ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                {missingPhotos} item{missingPhotos === 1 ? " has" : "s have"} no photo. Draw them
                with AI (about $0.04 each, first {Math.min(missingPhotos, 40)} now)?
              </span>
              <Button size="sm" variant="outline" loading={generating} onClick={onGenerateImages}>
                <Sparkles className="h-3.5 w-3.5" /> Generate photos
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
